import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useSessionPicker } from '../../../hooks/booking/useSessionPicker'
import { PassOwner, useBookingPricing } from '../../../hooks/booking/useBookingPricing'
import { CheckoutMode, useStartCheckout } from '../../../hooks/booking/useStartCheckout'
import { apiMessageKey } from '../../../hooks/useApiRequest'

import Icon from '../../../components/ui/Icon'
import { Notice } from '../../../components/guest/notice/Notice'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import SummaryPanel from '../../../components/authenticated/booking/summaryPanel/SummaryPanel'
import BookingLegend from '../../../components/authenticated/booking/bookingLegend/BookingLegend'
import SessionPicker, { SessionPickerSetup } from '../../../components/authenticated/booking/sessionPicker/SessionPicker'
import FundingOptions from '../../../components/authenticated/booking/fundingOptions/FundingOptions'
import BookingRecipientForm from '../../../components/authenticated/management/booking/managerBookings/bookingRecipientForm/BookingRecipientForm'
import BookingPageLayout from '../../../components/bookingPageLayout/BookingPageLayout'
import { CheckboxInput } from '../../../components/input/checkboxInput/CheckboxInput'

import { useApp } from '../../../contexts/AppContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { BookingLocationState } from '../../../types/booking/booking'
import { BookingRecipient, BookingRecipientType } from '../../../types/court/court'
import { PaymentMethod } from '../../../types/payment/payment'
import { formatPoints } from '../../../utils/booking/booking'

/**
 * Booking on behalf of a customer, confirmed immediately: the manager records a cash or bank-transfer payment (the
 * manager never pays by card for the customer). An existing customer's prepaid minutes are used first.
 */
const ManagerBooking = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [initialDraft] = useState(
    () => (location.state as BookingLocationState | null)?.bookingDraft
  )
  const { showBanner } = useApp()
  const { t } = useTranslation('bookings')
  const [recipient, setRecipient] = useState<BookingRecipient | undefined>(initialDraft?.recipient)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    initialDraft?.paymentMethod === PaymentMethod.BANK_TRANSFER ? PaymentMethod.BANK_TRANSFER : PaymentMethod.CASH
  )
  const [topUpPassProductId, setTopUpPassProductId] = useState<number | undefined>(
    initialDraft?.topUpPassProductId
  )
  const [useRefundPoints, setUseRefundPoints] = useState(initialDraft?.useRefundPoints ?? true)
  const existingProfileId =
    recipient?.type === BookingRecipientType.EXISTING_PROFILE ? recipient.profileId : undefined
  const passOwner: PassOwner = useMemo(
    () => (existingProfileId ? { profileId: existingProfileId } : null),
    [existingProfileId]
  )

  const picker = useSessionPicker({ initial: initialDraft })
  const pricing = useBookingPricing({
    sessions: picker.selected,
    clubId: picker.clubId,
    passOwner,
    topUpPassProductId: existingProfileId ? topUpPassProductId : undefined,
    recipient,
    useRefundPoints: Boolean(existingProfileId) && useRefundPoints,
    paymentMethod,
  })
  const { startCheckout } = useStartCheckout()

  const handleRecipientChange = useCallback(
    (nextRecipient?: BookingRecipient) => setRecipient(nextRecipient),
    []
  )

  useEffect(() => {
    if (initialDraft) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (picker.error) {
      showBanner({ kind: BannerKind.ERR, msg: picker.error })
      picker.resetError()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.error])

  const handleConfirm = async () => {
    if (!recipient || picker.selected.length === 0 || pricing.quoting) {
      return
    }
    // The server prices the selection once, here; the confirmation step shows its quote.
    let confirmed
    try {
      confirmed = await pricing.confirmQuote()
    } catch (caught) {
      if (apiMessageKey(caught) === 'availability-conflict') {
        picker.reload()
      }
      showBanner({ kind: BannerKind.ERR, msg: caught instanceof Error ? caught.message : String(caught) })
      return
    }
    const topUp = existingProfileId ? topUpPassProductId : undefined
    startCheckout({
      mode: CheckoutMode.MANAGER,
      sessions: picker.selected,
      lines: confirmed.lines,
      total: confirmed.total,
      pointsAmount: confirmed.pointsAmount,
      useRefundPoints: Boolean(existingProfileId) && useRefundPoints,
      topUpPassProductId: topUp,
      topUpName: confirmed.quote.topUpOptions.find(option => option.id === topUp)?.name,
      recipient,
      paymentMethod,
      returnTo: '/m/booking',
      returnState: {
        bookingDraft: { ...picker.draft, recipient, paymentMethod, topUpPassProductId, useRefundPoints },
      } satisfies BookingLocationState,
      onConflict: picker.reload,
    })
  }

  return (
    <BookingPageLayout
      header={
        <PageHeader
          title={{
            eyebrow: t('booking.manager-booking.page-header.eyebrow'),
            h1: t('booking.manager-booking.page-header.title'),
          }}
          sub={t('booking.manager-booking.page-header.subtitle')}
        />
      }
      bookingSetup={
        <>
          {/* Sessions can only be picked once the recipient is known. */}
          {!recipient && (
            <Notice
              className="fly-notice-warning mt-0 mb-3"
              icon={<Icon name="warn" size={18} />}
              title={t('booking.manager-booking.choose-customer-title')}
            >
              {t('booking.manager-booking.choose-customer')}
            </Notice>
          )}
          <BookingRecipientForm
            initialRecipient={initialDraft?.recipient}
            onChange={handleRecipientChange}
          />
          <div className="divider" />
        </>
      }
      toolbar={<SessionPickerSetup picker={picker} />}
      courts={
        <SessionPicker picker={picker} anyDay disabled={!recipient} isIncluded={pricing.isIncluded} />
      }
      legend={<BookingLegend />}
      summary={
        <SummaryPanel
          lines={pricing.lines}
          total={pricing.amountDue}
          pointsAmount={pricing.pointsAmount}
          confirmLabel={t('booking.booking-courts.summary-panel.confirm')}
          confirmDisabled={!recipient || !pricing.ready}
          loading={pricing.quoting}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          onConfirm={handleConfirm}
          onClear={picker.clear}
          options={
            existingProfileId && picker.selected.length > 0 ? (
              <>
                <FundingOptions
                  plan={pricing.plan}
                  topUpPassProductId={topUpPassProductId}
                  onTopUpChange={setTopUpPassProductId}
                  disabled={pricing.quoting}
                />
                {pricing.availablePoints > 0 && (
                  <CheckboxInput
                    name="useRefundPoints"
                    isSwitch
                    className="mt-8"
                    checked={useRefundPoints}
                    disabled={pricing.quoting}
                    label={t('booking.manager-booking.use-refund-points', {
                      points: formatPoints(pricing.availablePoints),
                    })}
                    onChange={(_, value) => setUseRefundPoints(value)}
                  />
                )}
              </>
            ) : undefined
          }
        />
      }
    />
  )
}

export default ManagerBooking
